import { api, type CronLog, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, statusTone } from '../../lib/format';
import { Button, Chip } from '../../ui';

const SYSTEM_TABS = [
  { label: 'News', to: '/system/news' },
  { label: 'Settings', to: '/system/settings' },
  { label: 'Cron Monitor', to: '/system/cron-log' },
];

const col = createColumnHelper<CronLog>();

/**
 * The income engine runs as scheduled jobs. This is the operator's view of
 * whether each daily/monthly run actually claimed its lock and completed —
 * the incident that motivated the idempotency work was invisible without it.
 */
export function CronMonitor() {
  const { query, setPage } = useTableQuery();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.cronLog(query),
    queryFn: () => api.admin.cronLog(query),
    refetchInterval: 30_000,
  });

  const clear = useMutation({
    mutationFn: () => api.admin.clearCronLog(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cron-log'] }),
  });

  const columns = useMemo(
    () => [
      col.accessor('job', { header: 'Job' }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('ran_for', { header: 'Ran for', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('duration_ms', {
        header: 'Duration',
        cell: (i) => (i.getValue() == null ? '—' : `${i.getValue()} ms`),
      }),
      col.accessor('message', { header: 'Message', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('created_at', { header: 'At', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Cron Monitor"
        description="Scheduled income-engine runs"
        tabs={SYSTEM_TABS}
        actions={
          <Button variant="danger" onClick={() => clear.mutate()} disabled={clear.isPending}>
            Clear log
          </Button>
        }
      />
      <DataTable<CronLog>
        title="Runs"
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
